import React, { useState, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Box, IconButton, InputAdornment, MenuItem, Select, Skeleton, TextField, Tooltip, Typography } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import ContactPhoneOutlined from '@mui/icons-material/ContactPhoneOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import ImageInput from "../../common/ImageInput";
import { withSnackbar } from "../../utils/SnackbarHOC";
import { addUser, editUser, getDetails, getRole } from "../../services/UserService";
import { AppButton } from "../../components/UI";

// The API can return the string "null" for empty values.
const clean = (v) => (v === null || v === undefined || v === 'null' || v === 'undefined' ? '' : v);

const roleLabel = (role = '') => role.replace(/ROLE_/g, '').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

const LOCATIONS = [{ value: 'rabat', label: 'Rabat' }, { value: 'paris', label: 'Paris' }];

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff' } };

const Field = ({ label, required, error, hint, children }) => (
  <Box>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: error ? '#dc2626' : '#334155', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.75 }}>
      {label} {required && <Box component="span" sx={{ color: '#dc2626' }}>*</Box>}
    </Typography>
    {children}
    {error
      ? <Typography sx={{ fontSize: '0.76rem', color: '#dc2626', mt: 0.5 }}>{error}</Typography>
      : hint && <Typography sx={{ fontSize: '0.74rem', color: '#94a3b8', mt: 0.5 }}>{hint}</Typography>}
  </Box>
);

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <Box sx={{ p: 2.5, borderRadius: '14px', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.05)', display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ebf4fb', color: '#03407b' }}>
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{subtitle}</Typography>}
      </Box>
    </Box>
    {children}
  </Box>
);

const FormUser = ({ editMode, snackbarShowMessage }) => {
  const history = useHistory();
  const [titre, setTitre] = useState("");
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [localisation, setLocalisation] = useState("");
  const [fonction, setFonction] = useState("");
  const [telFix, setTelFix] = useState("");
  const [telMobile, setTelMobile] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [statut, setStatut] = useState("");
  const [roles, setRoles] = useState([]);
  const [ringOverToken, setRingOverToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  const [errors, setErrors] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const { id } = useParams();

  const validate = () => {
    let temp = {};
    temp.titre = titre ? "" : "Choisissez la civilité.";
    temp.prenom = prenom ? "" : "Le prénom est obligatoire.";
    temp.nom = nom ? "" : "Le nom est obligatoire.";
    temp.localisation = localisation ? "" : "Choisissez la localisation.";
    temp.fonction = fonction ? "" : "La fonction est obligatoire.";
    temp.telMobile = /^(?:(?:\+|00)33|0)[1-9](?:[0-9]{2}){4}$/.test(String(telMobile).replace(/\s/g, ''))
      ? ""
      : "Numéro invalide. Format attendu : 06 12 34 56 78 ou +33 6 12 34 56 78.";
    temp.email =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email)
        ? ""
        : "Adresse email invalide.";
    if (!editMode) {
      temp.password = password ? "" : "Le mot de passe est obligatoire.";
    }
    temp.statut = statut ? "" : "Choisissez le rôle de l'utilisateur.";

    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  // Never send "null"/"undefined" strings: FormData turns null and undefined into text.
  const buildFormData = () => {
    const formData = new FormData();
    formData.append('titre', titre);
    formData.append('nom', nom);
    formData.append('prenom', prenom);
    formData.append('localisation', localisation);
    formData.append('fonction', fonction);
    formData.append('tel_fixe', clean(telFix));
    formData.append('tel_mobile', String(telMobile).replace(/\s/g, ''));
    formData.append('email', email);
    formData.append('role', statut);
    formData.append('ringOverKey', clean(ringOverToken));
    // On edit, only send a password when a new one was typed.
    if (password) formData.append('plainPassword', password);
    if (selectedFile) formData.append('image', selectedFile);
    return formData;
  };

  const save = async () => {
    if (!validate()) {
      snackbarShowMessage("Certains champs sont à corriger.", "error", 3000);
      return;
    }
    setLoading(true);
    try {
      snackbarShowMessage("Patientez svp...", "warning", 4000);
      if (editMode) await editUser(buildFormData(), id);
      else await addUser(buildFormData());
      history.goBack();
      snackbarShowMessage(editMode ? "Bien modifié." : "Bien ajouté.", "success", 4000);
    } catch (error) {
      if (!mounted.current) return;
      snackbarShowMessage(editMode ? "Erreur de modification d'utilisateur." : "Erreur d'ajout d'utilisateur.", "error", 4000);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  React.useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        setLoadingData(true);
        const roles = await getRole();
        setRoles(roles.data);
        if (editMode) {
          const { data } = await getDetails(id);
          setTitre(clean(data.titre));
          setPrenom(clean(data.prenom));
          setNom(clean(data.nom));
          setLocalisation(clean(data.localisation));
          setFonction(clean(data.fonction));
          setTelFix(clean(data.telFixe));
          setTelMobile(clean(data.telMobile));
          setEmail(clean(data.email));
          setStatut(clean(data.statut));
          setRingOverToken(clean(data.ringOverKey));
          setImageName(data.image);
        }
      } catch (error) {
        snackbarShowMessage("Erreur de chargement des données.", "error", 4000);
      } finally {
        setLoadingData(false);
      }
    })();
    return () => { mounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fullName = [prenom, nom].filter(Boolean).join(' ');

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Tooltip title="Retour">
          <IconButton onClick={() => history.goBack()} sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', background: '#fff', color: '#03407b' }}>
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
            {editMode ? "Modifier un utilisateur" : "Ajouter un utilisateur"}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
            {editMode ? (fullName || 'Chargement…') : "Créez un accès au CRM pour un membre de l'équipe"}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <AppButton variant="secondary" onClick={() => history.goBack()} disabled={loading}>Annuler</AppButton>
          <AppButton variant="primary" onClick={save} loading={loading} disabled={loadingData} startIcon={<SaveOutlined fontSize="small" />}>
            {editMode ? "Enregistrer" : "Créer l'utilisateur"}
          </AppButton>
        </Box>
      </Box>

      {loadingData ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={230} sx={{ borderRadius: '14px' }} />)}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
          <Section icon={BadgeOutlined} title="Identité" subtitle="Comment l'utilisateur apparaît dans le CRM">
            <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 1.5 }}>
              <Field label="Civilité" required error={errors.titre}>
                <Select fullWidth size="small" displayEmpty value={titre} onChange={(e) => setTitre(e.target.value)} error={!!errors.titre} sx={{ borderRadius: '10px' }}>
                  <MenuItem value="" disabled><em>—</em></MenuItem>
                  <MenuItem value="Mr">Mr</MenuItem>
                  <MenuItem value="Mme">Mme</MenuItem>
                </Select>
              </Field>
              <Field label="Prénom" required error={errors.prenom}>
                <TextField fullWidth size="small" autoComplete="off" placeholder="Prénom" value={prenom}
                  onChange={(e) => setPrenom(e.target.value.toLowerCase())} error={!!errors.prenom} sx={inputSx} />
              </Field>
            </Box>
            <Field label="Nom" required error={errors.nom}>
              <TextField fullWidth size="small" autoComplete="off" placeholder="NOM" value={nom}
                onChange={(e) => setNom(e.target.value.toUpperCase())} error={!!errors.nom} sx={inputSx} />
            </Field>
            <Field label="Fonction" required error={errors.fonction}>
              <TextField fullWidth size="small" autoComplete="off" placeholder="Ex : Talent manager" value={fonction}
                onChange={(e) => setFonction(e.target.value)} error={!!errors.fonction} sx={inputSx} />
            </Field>
            <Field label="Photo" hint="Optionnel · affichée dans le menu utilisateur et sur les tâches">
              <ImageInput selectedFile={selectedFile} setSelectedFile={setSelectedFile} imageName={imageName} />
            </Field>
          </Section>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Section icon={AdminPanelSettingsOutlined} title="Accès au CRM" subtitle="Identifiant de connexion et droits">
              <Field label="Email (identifiant)" required error={errors.email}>
                <TextField fullWidth size="small" autoComplete="off" type="email" placeholder="prenom.nom@goandev.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} error={!!errors.email} sx={inputSx} />
              </Field>
              <Field
                label={editMode ? "Nouveau mot de passe" : "Mot de passe"}
                required={!editMode}
                error={errors.password}
                hint={editMode ? "Laissez vide pour conserver le mot de passe actuel." : undefined}
              >
                <TextField
                  fullWidth size="small" autoComplete="new-password" value={password}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={editMode ? "••••••••" : "Mot de passe"}
                  onChange={(e) => setPassword(e.target.value)} error={!!errors.password} sx={inputSx}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPassword((v) => !v)} aria-label="Afficher le mot de passe">
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Field>
              <Field label="Rôle" required error={errors.statut} hint="Détermine les pages et actions accessibles.">
                <Select fullWidth size="small" displayEmpty value={roles.some((r) => r.uuid === statut) ? statut : ''}
                  onChange={(e) => setStatut(e.target.value)} error={!!errors.statut} sx={{ borderRadius: '10px' }}
                  renderValue={(v) => v ? roleLabel(roles.find((r) => r.uuid === v)?.role) : <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>Choisir un rôle…</Typography>}>
                  {roles.map((role) => (
                    <MenuItem value={role.uuid} key={role.uuid}>{roleLabel(role.role)}</MenuItem>
                  ))}
                </Select>
              </Field>
            </Section>

            <Section icon={ContactPhoneOutlined} title="Coordonnées">
              <Field label="Téléphone mobile" required error={errors.telMobile}>
                <TextField fullWidth size="small" autoComplete="off" placeholder="06 12 34 56 78" value={telMobile}
                  onChange={(e) => setTelMobile(e.target.value)} error={!!errors.telMobile} sx={inputSx} />
              </Field>
              <Field label="Localisation" required error={errors.localisation}>
                <Box role="radiogroup" sx={{ display: 'flex', gap: 1 }}>
                  {LOCATIONS.map((l) => {
                    const active = localisation === l.value;
                    return (
                      <Box key={l.value} role="radio" aria-checked={active} tabIndex={0}
                        onClick={() => setLocalisation(l.value)}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setLocalisation(l.value)}
                        sx={{
                          flex: 1, textAlign: 'center', py: 0.9, borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem',
                          color: active ? '#fff' : '#475569', background: active ? '#03407b' : '#fff',
                          border: `1.5px solid ${active ? '#03407b' : errors.localisation ? '#fca5a5' : '#e2e8f0'}`,
                          '&:hover': { borderColor: '#03407b' }, '&:focus-visible': { outline: '2px solid #aed6f1' },
                        }}>
                        {l.label}
                      </Box>
                    );
                  })}
                </Box>
              </Field>
            </Section>

            <Section icon={ExtensionOutlined} title="Intégrations" subtitle="Optionnel">
              <Field label="Token Ringover" hint="Permet d'appeler les candidats depuis le CRM via Ringover.">
                <TextField
                  fullWidth size="small" autoComplete="off" placeholder="Aucun token configuré" value={ringOverToken}
                  type={showToken ? 'text' : 'password'}
                  onChange={(e) => setRingOverToken(e.target.value)} sx={inputSx}
                  slotProps={{
                    input: {
                      endAdornment: ringOverToken ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowToken((v) => !v)} aria-label="Afficher le token">
                            {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    },
                  }}
                />
              </Field>
            </Section>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default withSnackbar(FormUser);
